// Modified from @zapier/design-system/src/components/display/Modal/useFocusTrap
import { useCallback, useEffect, useState } from "react";

const focusableNodeSelector = [
  "a[href]",
  "button",
  "input",
  "textarea",
  "select",
  "details",
  "[tabindex]",
].join(", ");

const enabledSelector = ':not([disabled]):not([tabindex="-1"])';

export const findFocusableNodesWithin = (
  contextNode: HTMLDivElement | Document
) => {
  return Array.from(
    contextNode.querySelectorAll<HTMLElement>(focusableNodeSelector)
  ).filter((node) => {
    return node.matches(enabledSelector);
  });
};

// Find the first or last node within the modal that can receive focus
// based on whether `focusTarget` appears before or after the modal node
// within the DOM.
const findFocusTrapNodeToFocus = (
  modalNode: HTMLDivElement,
  focusTarget: HTMLElement
) => {
  const allFocusableNodes = findFocusableNodesWithin(document);
  const focusableNodesWithinModal = findFocusableNodesWithin(modalNode);
  const firstFocusableModalNode = focusableNodesWithinModal[0];
  const lastFocusableModalNode =
    focusableNodesWithinModal[focusableNodesWithinModal.length - 1];
  if (!firstFocusableModalNode) {
    return undefined;
  }
  const focusTargetIndex = allFocusableNodes.indexOf(focusTarget);
  const firstFocusableModalNodeIndex = allFocusableNodes.indexOf(
    firstFocusableModalNode
  );
  return focusTargetIndex < firstFocusableModalNodeIndex
    ? lastFocusableModalNode
    : firstFocusableModalNode;
};

export const useFocusTrap = () => {
  const [focusTrapElement, setFocusTrapElement] =
    useState<HTMLDivElement | null>(null);

  const focusTrapRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    setFocusTrapElement(node);
    const firstFocusableModalNode = findFocusableNodesWithin(node)?.[0];

    firstFocusableModalNode?.focus();
  }, []);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const focusTarget = e.target as HTMLElement;
      if (
        focusTarget &&
        focusTrapElement &&
        !focusTrapElement.contains(focusTarget)
      ) {
        const nodeToFocus = findFocusTrapNodeToFocus(
          focusTrapElement,
          focusTarget
        );
        if (nodeToFocus) {
          nodeToFocus.focus();
        }
      }
    };
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [focusTrapElement]);

  return { focusTrapRef };
};
